/**
 * API-Client der Web-App.
 * - Das Zugriffstoken (15 Minuten) liegt nur im Speicher, nie in localStorage (XSS könnte es lesen).
 * - Das Refresh-Token ist ein httpOnly-Cookie und für JavaScript unsichtbar.
 * - Bei 401 wird EINMAL erneuert (auch bei mehreren gleichzeitigen Anfragen nur ein Aufruf) und
 *   die Anfrage wiederholt. Scheitert das, ist die Sitzung zu Ende.
 */

import { isLogoutPending } from '../auth/logoutPending';
import { connectivity } from '../lib/connectivity';

const BASE = '/api/v1';

/**
 * Zeitlimits (SYN-04, 01-ARCHITEKTUR §10): Ohne sie hängt eine Anfrage im WLAN ohne Internet („Lie-Fi“) bis zum Zeitlimit des
 * Betriebssystems und blockiert Sync und App-Start. Das Limit gilt für die ganze Anfrage bis zum Eintreffen der Antwort.
 */
export const TIMEOUT_GET_MS = 15_000;
export const TIMEOUT_REFRESH_MS = 8_000;
export const TIMEOUT_MUTATION_MS = 30_000;
/** Dateien (PDF, Excel) entstehen auf dem Server, z. B. Etikettenbögen für hunderte Artikel; der Körper selbst hat kein Limit. */
export const TIMEOUT_DOWNLOAD_MS = 120_000;
/** Lange Änderungen: Hochladen (Plakat, Foto), Import von Artikeln oder einer Kassendatei, Abrechnung, Basar kopieren. */
export const TIMEOUT_LONG_MS = 120_000;
/** Abmelden darf nicht lange warten: Die Oberfläche räumt danach ohnehin auf. */
export const TIMEOUT_LOGOUT_MS = 8_000;

/** Sofortige Absage ohne Anfrage, wenn das Gerät sicher offline ist (`navigator.onLine === false`). */
export const OFFLINE_MESSAGE =
  'Offline: nicht gesendet. Bitte erneut versuchen, sobald Netz da ist.';
const TIMEOUT_MESSAGE =
  'Zeitüberschreitung: Der Server antwortet nicht. Bitte prüfen Sie Ihr Netz und versuchen Sie es erneut.';
/** Eine Änderung, deren Antwort ausblieb, kann trotzdem angekommen sein: nicht „nicht gespeichert“ behaupten. */
const TIMEOUT_UNCERTAIN_MESSAGE =
  'Zeitüberschreitung: Das Ergebnis ist unklar. Bitte prüfen Sie die Liste, bevor Sie es erneut versuchen.';

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details: unknown = null) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface ErrorBody {
  error?: { code?: unknown; message?: unknown; details?: unknown };
}

let accessToken: string | null = null;
let refreshInFlight: Promise<RefreshOutcome> | null = null;
let onSessionEnded: () => void = () => undefined;

/**
 * Prüft nach einem stillen Erneuern über das Cookie, ob das neue Token der Person dieses Tabs gehört (`sub`; `null`, wenn nicht
 * lesbar). Bei `false` wird das neue Token verworfen und nichts wiederholt: Das Cookie gehört im gemeinsamen Browser einer
 * anderen Person (Anmeldung in einem anderen Tab, ohne dass dieser Tab es über den Speicher erfahren hat).
 */
let refreshOwnerCheck: (owner: string | null) => boolean = () => true;

export function setRefreshOwnerCheck(check: (owner: string | null) => boolean): void {
  refreshOwnerCheck = check;
}

/** Person (`sub`) eines Zugriffstokens; nur gelesen, nicht geprüft (die Prüfung macht der Server). */
export function tokenOwner(token: string): string | null {
  try {
    const part = token.split('.')[1];
    if (part === undefined) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = new TextDecoder().decode(
      Uint8Array.from(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')), (c) =>
        c.charCodeAt(0),
      ),
    );
    const sub = (JSON.parse(json) as { sub?: unknown }).sub;
    return typeof sub === 'string' && sub !== '' ? sub : null;
  } catch {
    return null;
  }
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Wird aufgerufen, wenn die Sitzung nicht mehr erneuerbar ist (die Oberfläche zeigt dann die Anmeldung). */
export function setSessionEndedHandler(handler: () => void): void {
  onSessionEnded = handler;
}

function toError(status: number, body: unknown): ApiClientError {
  const error = (body as ErrorBody | null)?.error;
  return new ApiClientError(
    status,
    typeof error?.code === 'string' ? error.code : 'UNKNOWN',
    typeof error?.message === 'string' ? error.message : 'Etwas ist schiefgelaufen.',
    error?.details ?? null,
  );
}

/** Zeitlimit einer Anfrage und ob ein Ausbleiben der Antwort das Ergebnis offen lässt (Änderung ohne Idempotenz). */
interface Limits {
  timeoutMs: number;
  uncertain: boolean;
}

/** Ein Gateway-Fehler (502 bis 504) heißt: Der Server selbst antwortet nicht, nur der Proxy davor. */
const isGatewayFailure = (status: number): boolean => status >= 502 && status <= 504;

async function send(
  method: string,
  path: string,
  body: unknown,
  withToken: boolean,
  accept = 'application/json',
  extraHeaders: Readonly<Record<string, string>> = {},
  signal?: AbortSignal,
  limits: Limits = { timeoutMs: TIMEOUT_GET_MS, uncertain: false },
): Promise<Response> {
  // Sicher offline: gar nicht erst versuchen (kein Warten auf ein Zeitlimit)
  if (!navigator.onLine) throw new ApiClientError(0, 'NETWORK', OFFLINE_MESSAGE);
  const headers: Record<string, string> = { Accept: accept, ...extraHeaders };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (withToken && accessToken !== null) headers['Authorization'] = `Bearer ${accessToken}`;
  // Eigenes Zeitlimit mit AbortController und setTimeout (läuft auch in älteren WebViews ohne `AbortSignal.any`)
  const controller = new AbortController();
  const state = { timedOut: false };
  const timer = setTimeout(() => {
    state.timedOut = true;
    controller.abort();
  }, limits.timeoutMs);
  const relay = (): void => {
    controller.abort();
  };
  if (signal?.aborted === true) controller.abort();
  else signal?.addEventListener('abort', relay, { once: true });
  try {
    const response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      credentials: 'same-origin',
      signal: controller.signal,
    });
    connectivity.reportResult(!isGatewayFailure(response.status));
    return response;
  } catch {
    // Ein vom Aufrufer selbst abgebrochener Aufruf (veraltete Suche) sagt nichts über das Netz
    if (!state.timedOut && signal?.aborted === true) {
      throw new ApiClientError(0, 'NETWORK', 'Die Anfrage wurde abgebrochen.');
    }
    connectivity.reportResult(false);
    throw new ApiClientError(
      0,
      'NETWORK',
      state.timedOut
        ? limits.uncertain
          ? TIMEOUT_UNCERTAIN_MESSAGE
          : TIMEOUT_MESSAGE
        : 'Keine Verbindung zum Server. Bitte prüfen Sie Ihr Netz und versuchen Sie es erneut.',
    );
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', relay);
  }
}

/** Erneuert das Zugriffstoken über das Refresh-Cookie. Gibt `false` zurück, wenn keine Sitzung besteht. */
export async function refreshSession(): Promise<boolean> {
  return (await refreshOutcome()) === 'ok';
}

type RefreshOutcome = 'ok' | 'denied' | 'foreign';

function refreshOutcome(): Promise<RefreshOutcome> {
  // Abmeldung ohne Netz: Das Cookie ist auf dem Server noch gültig, soll aber nie wieder anmelden, bis der Logout nachgeholt ist
  if (isLogoutPending()) {
    accessToken = null;
    return Promise.resolve('denied');
  }
  refreshInFlight ??= (async () => {
    try {
      const response = await send(
        'POST',
        '/auth/refresh',
        undefined,
        false,
        undefined,
        undefined,
        undefined,
        {
          // Sicher wiederholbar (60-s-Wiederholungsfenster, SYN-11)
          timeoutMs: TIMEOUT_REFRESH_MS,
          uncertain: false,
        },
      );
      if (response.status === 401) {
        accessToken = null;
        return 'denied';
      }
      // Überlast (429), Serverfehler (5xx): die Sitzung ist NICHT zu Ende, der Aufrufer versucht es später erneut
      if (!response.ok) throw toError(response.status, await parse(response));
      const data = (await response.json()) as { accessToken: string };
      // Gehört das neue Token einer anderen Person als der dieses Tabs, gilt es nicht: Dieser Tab beendet seine Sitzung lokal
      if (!refreshOwnerCheck(tokenOwner(data.accessToken))) {
        accessToken = null;
        return 'foreign';
      }
      accessToken = data.accessToken;
      return 'ok';
    } finally {
      // Nach Abschluss wieder freigeben (das Promise selbst bleibt für die Wartenden gültig)
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();
  return refreshInFlight;
}

async function parse(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const text = await response.text();
  if (text === '') return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

export interface RequestOptions {
  /** `false` für öffentliche Routen (Anmeldung, Registrierung): kein Token, kein automatisches Erneuern. */
  auth?: boolean;
  /** Zusätzliche Kopfzeilen, z. B. die Basar-Sitzung der Kasse (`X-Register-Session`). */
  headers?: Readonly<Record<string, string>>;
  /** Bricht die Anfrage ab (z. B. veraltete Vorschlagssuche). */
  signal?: AbortSignal;
  /** Zeitlimit in Millisekunden (Standard: 15 s für Lesen, 30 s für Änderungen). */
  timeoutMs?: number;
  /**
   * Die Anfrage ist idempotent (z. B. Sync mit `client_uuid`): Ein Zeitlimit heißt dann sicher „nicht angekommen oder
   * wiederholbar“. Ohne diese Angabe meldet eine Änderung bei Zeitlimit „Ergebnis unklar“.
   */
  idempotent?: boolean;
  /**
   * Nach einem 401 wird nur dann still über das Cookie erneuert und wiederholt, wenn dies noch stimmt (z. B. dieselbe Person):
   * Sonst würde die Anfrage unter dem Konto einer anderen Person gesendet, die sich inzwischen angemeldet hat. Stimmt es nicht,
   * kommt das 401 ohne Erneuern und ohne Abmelden der Oberfläche zurück.
   */
  stillValid?: () => boolean;
}

/** Sendet die Anfrage; bei 401 wird einmal erneuert und wiederholt, danach gilt die Sitzung als beendet. */
async function sendWithRefresh(
  method: string,
  path: string,
  body: unknown,
  auth: boolean,
  accept?: string,
  extraHeaders?: Readonly<Record<string, string>>,
  signal?: AbortSignal,
  limits?: Limits,
  stillValid?: () => boolean,
): Promise<Response> {
  // Gilt die Anfrage schon vor dem Senden nicht mehr (andere Person, Sitzung weg), geht sie nie hinaus
  if (auth && stillValid !== undefined && !stillValid()) {
    throw new ApiClientError(401, 'UNAUTHORIZED', 'Die Anmeldung gilt nicht mehr.');
  }
  let response = await send(method, path, body, auth, accept, extraHeaders, signal, limits);
  if (response.status === 401 && auth) {
    if (stillValid !== undefined && !stillValid()) return response;
    const outcome = await refreshOutcome();
    // Fremdes Cookie: Der Tab hat seine Sitzung lokal beendet, das 401 geht zurück (kein Abmelden der gemeinsamen Daten)
    if (outcome === 'foreign') return response;
    if (outcome === 'ok') {
      if (stillValid !== undefined && !stillValid()) return response;
      response = await send(method, path, body, true, accept, extraHeaders, signal, limits);
    }
    if (response.status === 401) {
      accessToken = null;
      onSessionEnded();
    }
  }
  return response;
}

/** Lädt eine Datei (PDF, Excel) mit Anmeldung. Fehler kommen wie sonst als `ApiClientError` mit Klartext an. */
export async function apiDownload(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
  accept = 'application/pdf',
): Promise<{ blob: Blob; headers: Headers }> {
  const response = await sendWithRefresh(method, path, body, true, accept, undefined, undefined, {
    timeoutMs: TIMEOUT_DOWNLOAD_MS,
    uncertain: method !== 'GET',
  });
  if (!response.ok) throw toError(response.status, await parse(response));
  return { blob: await response.blob(), headers: response.headers };
}

export async function apiRequest<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const auth = options.auth ?? true;
  const response = await sendWithRefresh(
    method,
    path,
    body,
    auth,
    undefined,
    options.headers,
    options.signal,
    {
      timeoutMs: options.timeoutMs ?? (method === 'GET' ? TIMEOUT_GET_MS : TIMEOUT_MUTATION_MS),
      uncertain: method !== 'GET' && options.idempotent !== true,
    },
    options.stillValid,
  );
  const data = await parse(response);
  if (!response.ok) throw toError(response.status, data);
  return data as T;
}
